import { faChevronDown, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC, FormEvent, Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Checkbox, CheckboxGroup } from './checkbox';
import { useSearchParameters, useSetSearchParameters, useTerms } from './context';
import { isMobile, toIntArray } from './utils';
import styles from './work.module.scss';

const copyElement = (el: HTMLElement): HTMLElement => {
  const div = document.createElement('div');
  div.innerHTML = el.outerHTML;
  return div.firstElementChild as HTMLElement;
};

const getHeight = (el: HTMLElement): number => {
  const copy = copyElement(el);
  copy.style.position = 'absolute';
  copy.style.left = '-9999px';
  copy.style.width = `${el.clientWidth}px`;
  copy.style.height = '';
  copy.style.display = '';
  copy.style.visibility = 'hidden';
  document.body.appendChild(copy);
  const rect = copy.getBoundingClientRect();
  document.body.removeChild(copy);
  return rect.height;
};

export const WorksFilter: FC = () => {
  const terms = useTerms();
  const setSearch = useSetSearchParameters();
  const search = useSearchParameters();

  const [filters, setFilters] = useState({
    industry: search.industry.map((n) => n.toString(10)),
    option: search.option.map((n) => n.toString(10)),
    taste: search.taste.map((n) => n.toString(10)),
  });

  const filterRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(!isMobile());

  const onSubmit = useCallback(
    (evt: FormEvent) => {
      evt.preventDefault();

      const newSearch = {
        ...search,
        industry: toIntArray(filters.industry),
        option: toIntArray(filters.option),
        taste: toIntArray(filters.taste),
        page: 1,
      };

      setSearch(newSearch);
    },
    [search, filters, setSearch],
  );

  useLayoutEffect(() => {
    if (filterRef.current) {
      const height = getHeight(filterRef.current);
      filterRef.current.style.height = open ? `${height}px` : '0px';
    }
  }, [open]);

  useLayoutEffect(() => {
    const resizeHandler = () => {
      if (!filterRef.current) {
        return;
      }
      const height = getHeight(filterRef.current);
      filterRef.current.style.height = open ? `${height}px` : '0px';
    };
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [open]);

  useLayoutEffect(() => {
    let timeout: NodeJS.Timeout;
    if (filterRef.current) {
      const copy = copyElement(filterRef.current);
      copy.style.position = 'absolute';
      copy.style.left = '-9999px';
      copy.style.width = `${filterRef.current.clientWidth}px`;
      copy.style.height = '';
      copy.style.display = '';
      copy.style.visibility = 'hidden';
      document.body.appendChild(copy);
      const rect = copy.getBoundingClientRect();
      filterRef.current.style.height = open ? `${rect.height}px` : '0px';
      timeout = setTimeout(() => {
        document.body.removeChild(copy);
      }, 0);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  });

  return (
    <form onSubmit={onSubmit} className={styles.filter} data-open={open}>
      <button type='button' className={styles.filterToggle} onClick={() => setOpen((state) => !state)} data-open={open}>
        条件で絞り込む
        <FontAwesomeIcon icon={faChevronDown} className={styles.filterToggleIcon} />
      </button>
      <div ref={filterRef} className={styles.filterControls} data-open={open}>
        <dl className={styles.filterTable}>
          {(['industry', 'option', 'taste'] as const).map((key) => (
            <Fragment key={key}>
              <dt className={styles.filterTitle}>
                {{ industry: '業種', option: 'カスタマイズ', taste: 'デザイン' }[key]}
              </dt>
              <dd className={styles.filterCheckboxes}>
                <CheckboxGroup
                  value={filters[key]}
                  onChange={(values) => setFilters((state) => ({ ...state, [key]: values }))}
                >
                  {terms[key].map((term) => (
                    <Checkbox key={`${key}-${term.id}`} value={term.id.toString(10)}>
                      {term.name}
                    </Checkbox>
                  ))}
                </CheckboxGroup>
              </dd>
            </Fragment>
          ))}
        </dl>

        <div className={styles.filterButtons}>
          <button type='submit' className={styles.filterSubmit}>
            <FontAwesomeIcon icon={faSearch} />
            絞り込む
          </button>
          <button
            type='button'
            className={styles.filterReset}
            onClick={() => {
              setFilters({ industry: [], option: [], taste: [] });
            }}
          >
            条件をクリア
          </button>
        </div>
      </div>
    </form>
  );
};
